import React, {Fragment, useEffect, useState} from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {Button, Checkbox, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Tooltip} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import _ from "lodash";
import MessageBox from "../../util/messageBox";
import {getTableScrollbar} from '../../util/scrollbar';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.default,
    },
    dialogTitle: {
        textAlign: "center",
        padding: "10px"
    },
    dialogContent: {
        height: 350,
        maxHeight: 300,
    },
    title: {
        fontWeight: "bold"
    },
    rotate270: {
        transform: "rotate(270deg)"
    },
    rotate90: {
        transform: "rotate(90deg)"
    },
    rotate180: {
        transform: "rotate(180deg)"
    },
    paper: {
        height: 250,
        maxHeight: 250,
        overflow: 'auto',
        overflowX: "hidden",
    },
    agyleButton:{
        backgroundColor: "#448FFF",
    },
    agyleIconColor: {
        color: "#448FFF"
    }
}));

const PriorityOptions = (props) => {
    const classesLocal = useStyles();
    const {t, i18n} = useTranslation();
    const id = props.idField ? props.idField : "name";

    // List states
    const [disableList, setDisableList] = useState([]);
    const [activeList, setActiveList] = useState([]);

    // Toggle event
    const [toggleDisabled, setToggleDisabled] = useState(
        {
            active: [],
            disabled: []
        });

    const [openWarning, setOpenWarning] = useState(false);

    // Init Lists
    useEffect(() => {
        const disList = [];
        const activeList = [];
        if (!_.isEmpty(props.columns)) {
            _.forEach(props.columns, column => {
                if (!_.isNil(column.order)) {
                    activeList.push({...column});
                } else {
                    disList.push({...column});
                }
            });
        }
        setActiveList([...sortColumn(activeList)]);
        setDisableList([...disList]);
    }, []);

    const sortColumn = (columns) => {
        return _.sortBy(columns, obj => {
            return obj.order;
        });
    };

    // Checkbox list item handler
    const handleToggle = (value, type) => () => {
        const currentIndex = type === "active"
            ? toggleDisabled.active.indexOf(value)
            : toggleDisabled.disabled.indexOf(value);

        const newChecked = {...toggleDisabled};

        if (currentIndex === -1) {
            if (type === "active") {
                newChecked.active.push(value);
            } else {
                newChecked.disabled.push(value);
            }
        } else {
            if (type === "active") {
                newChecked.active.splice(currentIndex, 1);
            } else {
                newChecked.disabled.splice(currentIndex, 1);
            }
        }
        setToggleDisabled({...newChecked});
    };

    const save = () => {
        if (_.isEmpty(activeList)) {
            setOpenWarning(true);
        } else {
            props.saveColumns(activeList, disableList);
        }
    };

    const renderMoveButtons = () => {
        const moveByOne = (toUp, fast) => {
            let clone = _.cloneDeep(activeList);
            _.forEach(toggleDisabled.active, element => {
                const index = _.findIndex(clone, {[id]: element});
                if (index !== -1) {
                    const col = clone[index];
                    if (toUp) {
                        if (fast) {
                            _.remove(clone, {[id]: element});
                            clone.unshift(col);
                        } else {
                            if (index > 0) {
                                _.remove(clone, {[id]: element});
                                clone.splice(index - 1, 0, col);
                            }
                        }
                    } else {
                        if (fast) {
                            _.remove(clone, {[id]: element});
                            clone.push(col);
                        } else {
                            if (index < clone.length) {
                                _.remove(clone, {[id]: element});
                                clone.splice(index + 1, 0, col);
                            }
                        }
                    }
                }
                const index2 = _.findIndex(clone, {[id]: element});
            });
            setActiveList([...clone]);
        };

        return (
            <Tooltip title={toggleDisabled.active.length > 1 ? "Please select only one element" : ""}
                     placement={"right"}>
                <ButtonGroup
                    sx={{mt: 7}}
                    orientation="vertical"
                    aria-label="vertical button group"
                >
                    <Button onClick={() => moveByOne(true)} disabled={toggleDisabled.active.length > 1}>
                        <ArrowForwardIosIcon className={clsx(classesLocal.agyleIconColor, classesLocal.rotate270)}/>
                    </Button>
                    <Button onClick={() => moveByOne(true, true)} disabled={toggleDisabled.active.length > 1}>
                        <DoubleArrowIcon className={clsx(classesLocal.agyleIconColor, classesLocal.rotate270) }/>
                    </Button>
                    <Button onClick={() => moveByOne(false, true)} disabled={toggleDisabled.active.length > 1}>
                        <DoubleArrowIcon className={clsx(classesLocal.agyleIconColor, classesLocal.rotate90)}/>
                    </Button>
                    <Button onClick={() => moveByOne(false)} disabled={toggleDisabled.active.length > 1}>
                        <ArrowForwardIosIcon className={clsx(classesLocal.agyleIconColor, classesLocal.rotate90)}/>
                    </Button>
                </ButtonGroup>
            </Tooltip>
        );
    };

    const renderListButtons = () => {
        const moveAllElements = (toActive) => {
            if (toActive) {
                setActiveList(prevState => [...prevState, ...disableList]);
                setDisableList([]);
            } else {
                setActiveList([]);
                setDisableList(prevState => [...prevState, ...activeList]);
            }
            setToggleDisabled({
                active: [],
                disabled: []
            });
        };

        const moveSelectedElement = (toActive) => {
            const arr = [];
            let copy;
            if (toActive) {
                if (!_.isEmpty(toggleDisabled.disabled)) {
                    copy = _.cloneDeep(disableList);
                    _.forEach(toggleDisabled.disabled, col => {
                        const columnIndex = _.findIndex(disableList, {[id]: col});
                        if (columnIndex !== -1) {
                            arr.push({...disableList[columnIndex]});
                        }
                    });

                    _.forEach(arr, element => {
                        copy = _.filter(copy, filter => {
                            return filter[id] !== element[id];
                        });
                    });

                    setDisableList(copy);
                    setActiveList(prevState => [...prevState, ...arr]);
                }
            } else {
                if (!_.isEmpty(toggleDisabled.active)) {
                    copy = _.cloneDeep(activeList);
                    _.forEach(toggleDisabled.active, col => {
                        const columnIndex = _.findIndex(activeList, {[id]: col});
                        if (columnIndex !== -1) {
                            arr.push({...activeList[columnIndex]});
                        }
                    });

                    _.forEach(arr, element => {
                        copy = _.filter(copy, filter => {
                            return filter[id] !== element[id];
                        });
                    });

                    setActiveList(copy);
                    setDisableList(prevState => [...prevState, ...arr]);
                }
            }
            setToggleDisabled({
                active: [],
                disabled: []
            });
        };

        return (
            <ButtonGroup
                sx={{mt: 7}}
                orientation="vertical"
                aria-label="vertical button group"
            >
                <Button onClick={() => moveSelectedElement(true)}>
                    <ArrowForwardIosIcon className={clsx(classesLocal.agyleIconColor)}/>
                </Button>
                <Button onClick={() => moveAllElements(true)}>
                    <DoubleArrowIcon className={clsx(classesLocal.agyleIconColor)}/>
                </Button>
                <Button onClick={() => moveAllElements(false)}>
                    <DoubleArrowIcon className={clsx(classesLocal.agyleIconColor, classesLocal.rotate180)}/>
                </Button>
                <Button onClick={() => moveSelectedElement(false)}>
                    <ArrowForwardIosIcon className={clsx(classesLocal.agyleIconColor, classesLocal.rotate180)}/>
                </Button>
            </ButtonGroup>
        );
    };

    const renderList = (list, text, type) => {
        return (
            <div style={{margin: "0 15px"}}>
                <span className={classesLocal.title}>{text}</span>
                <Paper className={clsx(classesLocal.paper, getTableScrollbar())}>
                    <List sx={{width: 300, maxWidth: 300}}>
                        {list.map(col => (
                            <ListItem
                                key={col[id]}
                                disablePadding
                            >
                                <ListItemButton dense divider onClick={handleToggle(col[id], type)}>
                                    <ListItemIcon>
                                        <Checkbox
                                            edge="start"
                                            checked={type === "active"
                                                ? toggleDisabled.active.indexOf(col[id]) !== -1
                                                : toggleDisabled.disabled.indexOf(col[id]) !== -1}
                                            tabIndex={-1}
                                            disableRipple
                                            sx={{'&.Mui-checked': {
                                             color: "#448FFF"
                                            }
                                            }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText id={col[id]} primary={col.description}/>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </div>
        );
    };

    return (
        <Fragment>
            <DialogTitle className={clsx(classesLocal.background, classesLocal.dialogTitle)}>
                {props.title? props.title : t("Priority List Columns Settings")}
            </DialogTitle>

            <DialogContent className={clsx(classesLocal.background, classesLocal.dialogContent, "scroll-normal-white")} dividers>
                <Grid
                    container
                    direction={"row"}
                    alignContent={"center"}
                    justifyContent={"center"}
                    style={{height: "100%"}}
                >
                    {renderList(disableList, "Disabled column", "disable")}
                    {renderListButtons()}
                    {renderList(activeList, "Active column", "active")}
                    {renderMoveButtons()}
                </Grid>
            </DialogContent>

            <DialogActions>
                <div style={{flex: '1 0 0'}}>
                    <Button onClick={props.onClose}   color="tertiary">
                        {t("Close")}
                    </Button>
                </div>
                <Button
                    variant={"contained"}
                    onClick={props.default}
                    className={classesLocal.agyleButton}
                >
                    {t("Default Layout")}
                </Button>
                <Button onClick={save}   color="tertiary">
                    {t("Submit")}
                </Button>
            </DialogActions>

            <MessageBox
                title={t("Warning")}
                onClose={() => setOpenWarning(false)}
                message={t("Settings cannot be saved without active columns")}
                open={openWarning}
            />
        </Fragment>
    );
};

export default PriorityOptions;